import { defineStore } from 'pinia';
import { useNotificationsStore } from '@/stores/useNotifications';
import useAcvDatadog from '@/composables/useAcvDatadog';
import { notificationKeys } from '@/constants/notificationKeys';

const { addRumError } = useAcvDatadog();

// Fallback for any invite error that isn't a duplicate email
const GENERIC_INVITE_ERROR_MESSAGE =
  '<span class="tw-font-medium">Unable to add user.</span>  Please check user email and try again.';

// Used when a batch invite has more than one duplicate email
const DUPLICATE_EMAIL_ERROR_MESSAGE =
  '<span class="tw-font-medium">Unable to invite users.</span>  These email addresses are already associated with a user. Remove them and submit again.';

/**
 * The invite-user API nests a JSON-encoded string inside `errors`
 * (e.g. `{"errors":["{\"errors\":[{\"<email1>\":\"duplicate_email\",\"<email2>\":\"duplicate_email\"}]}"]}`),
 * so it has to be parsed a second time. Each parsed entry can hold one key per
 * duplicate email, not just one. Returns [] if that shape isn't there.
 */
const getDuplicateEmails = (error) => {
  const rawErrors = error?.data?.errors ?? [];

  return rawErrors.flatMap((rawError) => {
    try {
      const parsedError = JSON.parse(rawError);
      return (parsedError?.errors ?? []).flatMap((entry) =>
        Object.entries(entry)
          .filter(([, code]) => code === 'duplicate_email')
          .map(([email]) => email),
      );
    } catch {
      return [];
    }
  });
};

/** Names the duplicate email when there's exactly one, otherwise falls back to a generic message. */
const getInviteErrorMessage = (error) => {
  const duplicateEmails = getDuplicateEmails(error);

  // Single duplicate: call out the specific email
  if (duplicateEmails.length === 1) {
    return `<span class="tw-font-medium">Unable to invite user.</span>  ${duplicateEmails[0]} is already associated with a user. Remove it and submit again.`;
  }

  // Multiple duplicates in one batch invite: keep it generic rather than listing them all
  if (duplicateEmails.length > 1) {
    return DUPLICATE_EMAIL_ERROR_MESSAGE;
  }

  // No duplicate_email code found (or the error shape didn't parse)
  return GENERIC_INVITE_ERROR_MESSAGE;
};

export const useCarrierUsersStore = defineStore('carrierUsers', {
  state: () => {
    return {
      users: [],
      search: '',
      status: 'active',
      pagination: {
        size: 100,
      },
      currentPageIndex: 0,
      rowsPerPage: 100,
      dialog: {
        isDialogOpen: false,
        selectedUser: {},
        active: {
          dialogTitle: 'Delete User',
          dialogContent: '',
          acceptText: 'Delete User',
        },
        pending: {
          dialogTitle: 'Cancel Invitation',
          dialogContent: ``,
          acceptText: 'Send Cancel',
        },
      },
      updateUserRow: {
        isUpdateUserSheetOpen: false,
        selectedUser: {
          userName: '',
          phone: '',
          email: '',
          transUserId: 0,
          index: 0,
        },
      },
      testRow: {
        selectedUser: {},
      },
    };
  },
  actions: {
    async fetchUsers() {
      const { $api } = useNuxtApp();

      try {
        const response = await $api('api/carrier/users', {
          params: {
            statuses: this.status,
            page: this.currentPageIndex,
            size: this.rowsPerPage,
            search: this.search,
          },
        });

        this.$patch({
          pagination: response.data.pagination,
          users: response.data.content,
        });
      } catch (error) {
        addRumError(new Error('Failed to fetch carrier users'), {
          error,
          statuses: this.status,
          page: this.currentPageIndex,
          size: this.rowsPerPage,
          search: this.search,
        });
      }
    },
    async inviteUsers(payload) {
      if (!payload) {
        return;
      }

      const { $api } = useNuxtApp();
      let data = null;
      let error = null;
      try {
        data = await $api('api/carrier/invite-user', {
          method: 'POST',
          body: { users: payload },
        });
      } catch (err) {
        error = err;
      }

      const notificationsStore = useNotificationsStore();

      if (error) {
        const notificationParams = {
          notificationMessageTemplate: getInviteErrorMessage(error),
          variant: 'error',
          key: notificationKeys.errorInviteUser,
        };
        notificationsStore.addNotification(notificationParams);

        const customError = new Error('400 invite users');
        addRumError(customError, {
          payload,
          error: error,
          notificationKey: notificationKeys.errorInviteUser,
        });

        return;
      }

      if (data) {
        const notificationParams = {
          notificationMessageTemplate:
            'Your invite has been sent requesting this user to <span class="tw-font-medium">Sign Up for ACV</span>',
          variant: 'success',
          key: notificationKeys.successInviteUser,
        };
        notificationsStore.addNotification(notificationParams);
      }
    },
    async removeUser() {
      if (!this.dialog.selectedUser?.transUserId) {
        return;
      }

      const { $api } = useNuxtApp();
      const notificationsStore = useNotificationsStore();
      let data = null;
      let error = null;
      try {
        data = await $api('api/carrier/user', {
          method: 'DELETE',
          body: {
            id: this.dialog.selectedUser.transUserId,
          },
        });
      } catch (err) {
        error = err;
      }

      if (error) {
        const notificationParams = {
          notificationMessageTemplate:
            '<span class="tw-font-medium">Unable to delete user.</span>  Please check user email and try again.',
          variant: 'error',
          key: notificationKeys.errorDeleteUser,
        };
        notificationsStore.addNotification(notificationParams);

        const customError = new Error('404 delete user');
        addRumError(customError, {
          selectedUser: this.dialog.selectedUser,
          error: error,
          notificationKey: notificationKeys.errorDeleteUser,
        });

        return;
      }

      /**
       * If user is successfully removed update the user list by filtering user from array
       */
      if (data) {
        this.users = this.users.filter((_, index) => index !== this.dialog.selectedUser.index);

        this.pagination.totalElements -= 1;

        this.dialog.selectedUser = {};

        const notificationParams = {
          notificationMessageTemplate: 'User has been successfully removed',
          variant: 'success',
          key: notificationKeys.successInviteUser,
        };
        notificationsStore.addNotification(notificationParams);
      }
    },
    async updateUser() {
      const selectedUser = this.updateUserRow.selectedUser;
      if (!selectedUser.transUserId) {
        return;
      }

      const { $api } = useNuxtApp();
      const notificationsStore = useNotificationsStore();
      let data = null;
      let error = null;
      try {
        data = await $api('api/carrier/user', {
          method: 'PUT',
          body: {
            userId: selectedUser.transUserId,
            userName: selectedUser.userName,
            role: selectedUser.role,
            phone: selectedUser.phone,
          },
        });
      } catch (err) {
        error = err;
      }

      if (error) {
        const notificationParams = {
          notificationMessageTemplate:
            '<span class="tw-font-medium">Unable to update user information.</span>  Please check your connection and try again.',

          variant: 'error',
          key: notificationKeys.errorUpdateUser,
        };
        notificationsStore.addNotification(notificationParams);

        const customError = new Error('500 update user');
        addRumError(customError, {
          selectedUser: selectedUser,
          error: error,
          notificationKey: notificationKeys.errorUpdateUser,
        });

        return;
      }

      if (data) {
        const temp = this.users;

        this.users[selectedUser.index] = data.data;
        this.users = [...this.users];

        const notificationParams = {
          notificationMessageTemplate: 'User information has been updated.',
          variant: 'success',
          key: notificationKeys.successUpdateUser,
        };
        notificationsStore.addNotification(notificationParams);
      }
    },
    updateStatus(newStatus) {
      if (newStatus) {
        this.$patch({
          status: newStatus,
          pagination: {},
          rowsPerPage: 100,
          currentPageIndex: 0,
          search: '',
        });
      }
    },
    updateSearch(newSearch) {
      this.$patch({
        search: newSearch,
      });
    },
    updateRowsPerPage(newRowsPerPage) {
      if (newRowsPerPage) {
        this.$patch({
          rowsPerPage: newRowsPerPage,
        });
      }
    },
    updatePageIndex(newPageIndex) {
      this.$patch({
        currentPageIndex: newPageIndex,
      });
    },
  },
  getters: {
    hasMultipleOwners: (state) => state.users.filter((user) => user.role === 'Owner').length > 1,
    cancelPendingUserMessage: (state) =>
      `Do you want to cancel the invitation sent to ${state.dialog.selectedUser?.email}?`,
    cancelActiveUserMessage: () => 'Are you sure you want to delete this user?',
  },
});
